// Do not import anything since this class is used basically everywhere.

export class Util {
  static toDateString(datestr: string | null): string {
    if (!datestr)
      return '';
    return new Date(datestr).toDateString();
  }
  static ANONYMOUS_PHOTO_URL = '/assets/profiles/anonymous.svg';
  static DAY_MILLISECONDS = 24 * 60 * 60 * 1000;
  static OneToOneDelimiter = ' & ';

  static pojo(obj: any) {
    return JSON.parse(JSON.stringify(obj));
  }

  static ProfileImg(photoUrl: string | null): string {
    return photoUrl ?? this.ANONYMOUS_PHOTO_URL;
  }

  static niceBytes(x: any) {
    let l = 0, n = parseInt(x, 10) || 0;
    while (n >= 1024 && ++l) {
      n = n / 1024;
    }
    return (n.toFixed(n < 10 && l > 0 ? 1 : 0) + ' ' + ['bytes', 'KB', 'MB', 'GB'][l]);
  }

  static getRelativeDate(daysFromNow: number) {
    return new Date(new Date().getTime() + daysFromNow * Util.DAY_MILLISECONDS);
  }

  static nrMinAgo(created: Date) {
    let now = new Date();
    let diff = now.getTime() - new Date(created).getTime();
    let minutes = Math.floor(diff / 60000);
    return minutes;
  }

  static nrHoursAgo(created: Date) {
    return Math.round(this.nrMinAgo(created) / 60);
  }

  static isOneToOneRoom(roomName: string | null | undefined) {
    return roomName?.includes(Util.OneToOneDelimiter) ?? false;
  }

  static sleep(duration: number) {
    return new Promise(r => setTimeout(r, duration));
  }

  static getMentionedUserIds(text: string | undefined): string[] {
    if (text) {
      let userIds = text.match(/\(user:.+?\)/g)?.map(m => {
        let id = m.replace(/[()]/g, '').replace('user:', '')
        return id;
      })
      return userIds ?? [];
    } else {
      return [];
    }
  }

  static getTextInsideParenthesis(text: string | undefined): string | undefined {
    if (text) {
      let matches = text.match(/\(.+?\)/g);
      if (matches && matches.length > 0)
        return matches[0].replace('(', '').replace(')', '');
    }
    return text;
  }

  static getCertainDate(offsetFromNowMin: number): Date {
    return new Date(new Date().getTime() + offsetFromNowMin * 60000);
  }

  static getTimerTimeFormat(seconds: number): string {
    return new Date(seconds * 1000).toISOString().slice(14, 19);
  }

  static getDomain(email: string) {
    if (email.includes('@'))
      return email.split('@')[1];
    else
      return email;
  }

  static getStoragePathFromUrl(downloadUrl?: string | null): string | null {
    if (!downloadUrl)
      return null;

    if (downloadUrl.startsWith('gs://')) {
      return decodeURIComponent(downloadUrl.replace(/^gs:\/\/[^/]+\//, ''));
    }

    try {
      const parsedUrl = new URL(downloadUrl);
      const pathname = parsedUrl.pathname;

      const extractAfterSegment = (segment: string) => {
        const index = pathname.indexOf(segment);
        if (index === -1)
          return null;
        const encoded = pathname.substring(index + segment.length).replace(/^\/+/, '');
        return decodeURIComponent(encoded);
      };

      const firebaseApiPath = extractAfterSegment('/o/');
      if (firebaseApiPath)
        return firebaseApiPath;

      if (parsedUrl.hostname === 'storage.googleapis.com') {
        const trimmed = pathname.replace(/^\/+/, '');
        const [, ...rest] = trimmed.split('/');
        if (rest.length > 0)
          return decodeURIComponent(rest.join('/'));
      }
    } catch (error) {
      console.error('Failed to parse storage path from url', error);
    }

    return null;
  }

  static getTextAsPlain(messageText: string | undefined) {
    if (messageText) {
      // 새 형식: [이름](user:id), 기존 형식: @[이름](user:id) 모두 지원
      messageText = messageText.replace(/@?\[.+?\]\(user:.*?\)/g, (match, i) => {
        let name = match.substring(match.indexOf('[') + 1, match.indexOf(']'));
        return `${name}`;
      })
      if (messageText.includes('Sources:')) {
        return messageText.substring(0, messageText.indexOf('Sources:'));
      } else {
        return messageText;
      }
    }
    return "";
  }

  static hasSameData(a: object | undefined, b: object | undefined): boolean {
    if (a && !b || !a && b)
      return false;
    else if (!a && !b)
      return true;
    else
      return JSON.stringify(a) === JSON.stringify(b);
  }

  static isImageUrl(storageUrl: string | undefined) {
    const url = storageUrl?.toLowerCase();
    return url?.includes('.jpg?') || url?.includes('.png?') || url?.includes('.gif?') || url?.includes('.jpeg?') || url?.includes('.webp?') || url?.includes('.svg?');
  }

  static getTruncatedText(text: string | undefined, maxLength: number) {
    if (text && text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  }

  static getTruncatedFileName(fileName: string | undefined, maxLength: number) {
    if (fileName && fileName.length > maxLength) {
      return fileName.substring(0, maxLength) + '...' + fileName.substring(fileName.length - 4);
    }
    return fileName;
  }

  static getLocaleLanguageCode() {
    const browserLang = navigator.language || (navigator as any).userLanguage;
    return browserLang.toLowerCase().startsWith('ko') ? 'kr' : 'jp';
  }

  static getOfficialSiteUrl() {
    return 'https://nextpod.jp';
  }

  static isLocalDevelopment() {
    if (typeof window === 'undefined') return false;

    // Capacitor 네이티브 앱에서는 항상 프로덕션으로 취급
    // ESM 환경에서 require가 작동하지 않을 수 있으므로 글로벌 객체 체크
    if (typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.()) {
      return false;
    }

    const hostname = window.location.hostname;
    const port = parseInt(window.location.port || '0', 10);
    return hostname === 'localhost' || hostname === '127.0.0.1' || port > 1000;
  }

  static getAppUrl() {
    if (Util.isLocalDevelopment()) {
      const hostname = window.location.hostname;
      const port = window.location.port;
      return `http://${hostname}${port ? ':' + port : ''}`;
    }

    const metaEnv = (typeof import.meta !== 'undefined') ? (import.meta as any).env : {};
    const domain = metaEnv?.VITE_MAIN_DOMAIN ?? process.env.VITE_MAIN_DOMAIN;

    return `https://${domain}`;
  }
  static getPublicSoribotPageURL(publicId: string): string {
    const appUrl = Util.getAppUrl();
    return appUrl + `/b/${publicId}`;
  }

  static getPublicSecureShareURL(secureShareId: string): string {
    const appUrl = Util.getAppUrl();
    return appUrl + `/share/${secureShareId}`;
  }

  static getPublicSoribotAPIURL(): string {
    if (Util.isLocalDevelopment()) {
      const host = typeof window !== 'undefined' ? window.location.hostname : '127.0.0.1';
      return `http://${host}:5001/sori-asia/asia-northeast1/askSoribotAPI`;
    } else
      return `https://asia-northeast1-sori-asia.cloudfunctions.net/askSoribotAPI`;
  }

  static dateSortFunc(a: Date, b: Date) {
    return new Date(a).getTime() < new Date(b).getTime() ? 1 : -1
  }

  static getStringsBetweenPattern(text: string, startPattern: string, endPattern: string): string[] {
    const escapeRegex = (str: string) => str.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

    const escapedStartPattern = escapeRegex(startPattern);
    const escapedEndPattern = escapeRegex(endPattern);

    // Added 's' flag to the regular expression
    const regex = new RegExp(`${escapedStartPattern}(.*?)${escapedEndPattern}`, 'gs');
    let matches = text.match(regex);

    if (matches) {
      return matches.map(m => m.slice(startPattern.length, m.length - endPattern.length));
    } else {
      return [];
    }
  }
}

export function pojo(obj: any) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Message 저장용 POJO 변환
 * user, autoMessageUser 필드를 최소화 (id, name, photoUrl만 유지)
 * 저장소 약 80% 절감
 */
export function messagePojo(msg: any) {
  const obj = pojo(msg);

  // user 필드 최소화
  if (obj.user) {
    obj.user = {
      id: obj.user.id,
      name: obj.user.name,
      photoUrl: obj.user.photoUrl ?? null,
    };
  }

  // autoMessageUser 필드 최소화
  if (obj.autoMessageUser) {
    obj.autoMessageUser = {
      id: obj.autoMessageUser.id,
      name: obj.autoMessageUser.name,
      photoUrl: obj.autoMessageUser.photoUrl ?? null,
    };
  }

  return obj;
}

/**
 * RoomItem/ExternalShareLink 저장용 POJO 변환
 * user 필드를 최소화 (id, name, photoUrl만 유지)
 */
export function roomItemPojo(item: any) {
  const obj = pojo(item);

  // user 필드 최소화
  if (obj.user) {
    obj.user = {
      id: obj.user.id,
      name: obj.user.name,
      photoUrl: obj.user.photoUrl ?? null,
    };
  }

  return obj;
}

/**
 * Notification 저장용 POJO 변환
 * fromUser 필드를 최소화 (id, name, photoUrl만 유지)
 */
export function notificationPojo(notif: any) {
  const obj = pojo(notif);

  // fromUser 필드 최소화
  if (obj.fromUser) {
    obj.fromUser = {
      id: obj.fromUser.id,
      name: obj.fromUser.name,
      photoUrl: obj.fromUser.photoUrl ?? null,
    };
  }

  return obj;
}

// function shouldShowPopup(): boolean {
//   let lastSeenPopup = LocalStorage.get('download-popup-seen');
//   if (lastSeenPopup) {
//     let past = new Date(lastSeenPopup).getTime();
//     let now = new Date().getTime();
//     console.log("popup-past", past, "popup-now", now, (now-past))
//     if (now - past > 60000 ) {
//       return true;
//     }
//   }
//   return (!hasAlreadySeenPopup && !hasAlreadySeenBanner) || (!hasAlreadySeenPopup);
// }

// function shouldShowBanner(): boolean {
//   let lastSeenBanner = LocalStorage.get('download-banner-seen');
//   if (lastSeenBanner) {
//     let past = new Date(lastSeenBanner).getTime();
//     let now = new Date().getTime();
//     console.log("banner-past", past, "banner-now", now, (now-past))
//     if (now - past > 60000) {
//       return true;
//     }
//   }
//   return !!hasAlreadySeenPopup && !hasAlreadySeenBanner;
// }

